import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { tenderService, transferWindowService } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import { usePermission } from "../../hooks/usePermission";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useViewMode } from "../../hooks/useViewMode";
import { effectiveSourceTab } from "../../utils/tenderSourceTabs";
import { useClubWindowCountryDefault } from "../common/useClubWindowCountry";

export const useClubTenders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Card ↔ list view toggle — same shared hook the agent view uses, so club
  // and agency behave identically (localStorage + URL ?view_mode=).
  const [viewMode, changeViewMode] = useViewMode("requestsViewMode");

  // Refs to prevent repetitive API calls on mount/Strict Mode
  const fetchedCountriesRef = useRef(false);
  const fetchedWindowRef = useRef("");
  const lastFetchedTenderParams = useRef("");
  // Guards the tab-reset effect against its own mount run (see below).
  const mountedOnceRef = useRef(false);

  const [countries, setCountries] = useState([
    { name: "View All", flag: null, isViewAll: true },
  ]);

  // Permission checks
  const canCreateTenders = usePermission("create_tenders");
  const [activeMainTab, setActiveMainTab] = useState("my-tenders");
  const [activeSubTab, setActiveSubTab] = useState("tp-tenders");
  // Starts EMPTY, not on a hardcoded country. The page used to open on the
  // literal "Spain", which showed a German club the Spanish transfer window as
  // if it were its own. The club's own country is adopted below as soon as it
  // is known; until then (and if it never resolves) nothing is selected and the
  // status line shows its empty placeholder instead of a foreign window.
  const [selectedCountry, setSelectedCountry] = useState("");
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Transfer Window states
  const [isTransferWindowDropdownOpen, setIsTransferWindowDropdownOpen] =
    useState(false);
  const [transferWindow, setTransferWindow] = useState(null);
  const [transferWindowLoading, setTransferWindowLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const [filters, setFilters] = useState({
    market_value_from: "",
    market_value_to: "",
    division: "",
    position: "",
    nationality: "",
    min_age: "",
    max_age: "",
    status: "all",
    search: "",
    only_eu_player: false,
    only_free_players: false,
    sort_by: "",
    country: "",
    continent: "",
    type_of_tender: "all",
  });
  // ── Request lifecycle segment (My Requests only) ─────────────────────────
  // "active" is the page exactly as it always was. It is deliberately kept
  // OUT of the `filters` object: that object is reset in three places (on tab
  // change, by "Clear all filters", and by its own initialiser), and routing
  // the lifecycle through it would silently drop the open tab on every one of
  // them. A separate piece of state cannot be reset by accident.
  //
  // The wizard sends `state.lifecycle` when it saved a draft, so the list
  // opens where the draft actually is instead of on Active, where it is not.
  const [lifecycleTab, setLifecycleTab] = useState(
    location.state?.lifecycle === "drafts" ? "drafts" : "active"
  );
  const [lifecycleCounts, setLifecycleCounts] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState("pro");
  const [activePositionFilter, setActivePositionFilter] = useState("players");
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: debouncedSearchTerm,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearchTerm]);

  // Fetch transfer-window countries from API
  useEffect(() => {
    if (fetchedCountriesRef.current) return;
    fetchedCountriesRef.current = true;

    const fetchCountries = async () => {
      try {
        const result = await transferWindowService.getTransferWindowCountries(
          "mens_professional"
        );
        if (result.success && Array.isArray(result.data)) {
          const countriesData = [
            { name: "View All", flag: null, isViewAll: true },
            ...result.data.map((c) => ({
              name: c.name || c.value,
              flag: c.image_url,
              isViewAll: false,
            })),
          ];
          setCountries(countriesData);
        } else {
          fetchedCountriesRef.current = false;
        }
      } catch (error) {
        fetchedCountriesRef.current = false;
        console.error("Error fetching transfer-window countries:", error);
      }
    };

    fetchCountries();
  }, []);

  // Handle click outside for transfer window dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTransferWindowDropdownOpen(false);
        setSearchQuery(""); // Reset search when closing
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter countries based on search query
  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fetchTenders = useCallback(
    async (page = 1, signal) => {
      // Construct query parameters
      const params = new URLSearchParams();
      params.append("page", page);

      if (activeMainTab === "my-tenders") {
        params.append("mine", "true");
      }
      if (activeMainTab === "all-tenders") {
        params.append("view", "all");
      }
      // The TP / TP Lite switch is only offered on All Listed Requests. On the
      // tabs where it is not offered the query must not keep asking for the
      // source the user last picked over there, or My Requests would fetch the
      // club's own rows with is_lite=true and come back empty. The user's
      // choice stays in state and returns with the tab.
      params.append(
        "is_lite",
        effectiveSourceTab(activeMainTab, activeSubTab) === "tp-lite-tenders"
          ? "true"
          : "false"
      );

      Object.entries(filters).forEach(([key, value]) => {
        if (key === "sort_by" && value) return;
        if (typeof value === "boolean" && value === true) {
          params.append(key, "true");
        } else if (value && value !== "all" && value !== "" && value !== false) {
          params.append(key, value);
        }
      });

      // Lifecycle scope: EVERY tab sends its status parameter — active, draft
      // or archive (the backend maps 'archive' onto its terminal set,
      // ARCHIVE_STATUSES; the client never spells those statuses out).
      //
      // The Active tab used to send nothing so its query stayed byte-for-byte
      // the pre-lifecycle one — a deliberately TRANSITIONAL choice from the
      // Drafts package, made while the Archive tab's query was still
      // undefined. The archive handoff has since defined it, and its tabs are
      // DISJOINT lists (My Requests Drafts Archive.dc.html:607-609:
      // statusOf = {active:'active', drafts:'draft', archive:'closed'};
      // list = pool.filter(r => r.status === statusOf[s.life])). An ended
      // request therefore lives in the Archive tab ALONE and no longer
      // lingers on Active carrying an Archived badge.
      if (activeMainTab === "my-tenders") {
        params.delete("status");
        params.append(
          "status",
          lifecycleTab === "drafts"
            ? "draft"
            : lifecycleTab === "archive"
            ? "archive"
            : "active"
        );
      }

      if (activePositionFilter) {
        params.append("tender_category", activePositionFilter);
      }

      const sortValue = filters.sort_by || sortBy;
      if (sortValue === "pro") {
        params.append("recruiting_for", "professional");
      } else if (sortValue === "youth") {
        params.append("recruiting_for", "youth");
      }

      if (sortValue) params.append("sort_by", sortValue);

      // Prevent redundant identical fetches
      const paramsString = params.toString() + `&tab=${activeMainTab}`;
      if (lastFetchedTenderParams.current === paramsString && !signal) return;
      
      const previousParams = lastFetchedTenderParams.current;
      lastFetchedTenderParams.current = paramsString;

      setLoading(true);
      setError(null);

      try {
        const response =
          activeMainTab === "my-tenders"
            ? await tenderService.getClubTenders(params, signal)
            : activeMainTab === "saved"
            ? await tenderService.getSavedTenders(params, signal)
            : await tenderService.getAllTenders(params, signal);

        if (response.success) {
          setTenders(response.data);
          setPagination(response.pagination);
        } else {
          lastFetchedTenderParams.current = previousParams;
          setError(response.error);
        }
      } catch (err) {
        if (err.name === "AbortError" || err.message === "canceled") return;
        lastFetchedTenderParams.current = previousParams;
        setError("Failed to fetch requests. Please try again.");
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [
      activeMainTab,
      filters,
      sortBy,
      activeSubTab,
      activePositionFilter,
      lifecycleTab,
    ]
  );

  // The club's own country becomes the initial selection, once, as soon as the
  // profile and the country list have both arrived. A country the user picks
  // themselves is never overwritten.
  useClubWindowCountryDefault(countries, selectedCountry, setSelectedCountry);

  // Fetch transfer window data
  useEffect(() => {
    if (!selectedCountry || selectedCountry === "View All" || fetchedWindowRef.current === selectedCountry) return;
    const previousWindowRef = fetchedWindowRef.current;
    fetchedWindowRef.current = selectedCountry;

    const fetchTransferWindow = async () => {
      setTransferWindowLoading(true);
      try {
        const response = await transferWindowService.getCurrentWindow(
          selectedCountry,
          "mens_professional"
        );

        if (response.success && response.data) {
          setTransferWindow(response.data);
        } else {
          fetchedWindowRef.current = previousWindowRef;
        }
      } catch (error) {
        fetchedWindowRef.current = previousWindowRef;
        console.error("Error fetching transfer window:", error);
      } finally {
        setTransferWindowLoading(false);
      }
    };

    fetchTransferWindow();
  }, [selectedCountry]);

  const formatDateRange = (windowData) => {
    if (!windowData) return "";
    if (windowData.current_start && windowData.current_end) {
      const startDate = new Date(windowData.current_start);
      const endDate = new Date(windowData.current_end);
      const formatDate = (date) => {
        return date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      };
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    return "";
  };

  const handleTransferWindowDropdownToggle = () => {
    setIsTransferWindowDropdownOpen(!isTransferWindowDropdownOpen);
    if (!isTransferWindowDropdownOpen) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleCountrySelect = (country) => {
    if (country.isViewAll) {
      navigate("/transfer-windows");
    } else {
      setSelectedCountry(country.name);
    }
    setIsTransferWindowDropdownOpen(false);
    setSearchQuery("");
  };

  // Reset filters when changing tabs
  useEffect(() => {
    setFilters({
      market_value_from: "",
      market_value_to: "",
      division: "",
      position: "",
      nationality: "",
      min_age: "",
      max_age: "",
      status: "all",
      search: "",
      only_eu_player: false,
      only_free_players: false,
      sort_by: "",
      country: "",
      continent: "",
      type_of_tender: "all",
    });
    setSearchTerm("");
    setSortBy("pro");
    setActivePositionFilter("players");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // Leaving "My Requests" leaves the lifecycle behind with it: the segment
    // does not exist on All Listed / Saved, so coming back must land on
    // Active rather than on a tab that was invisible in the meantime.
    //
    // Not on the FIRST run though: this effect also fires on mount, and would
    // then throw away the tab the wizard just asked for after saving a draft.
    if (mountedOnceRef.current) {
      setLifecycleTab("active");
    } else {
      mountedOnceRef.current = true;
    }
  }, [activeMainTab]);

  // Fetch tenders when tab changes or filters change
  useEffect(() => {
    const controller = new AbortController();
    fetchTenders(pagination.currentPage, controller.signal);

    return () => {
      controller.abort();
    };
  }, [fetchTenders, pagination.currentPage]);

  // ── Live lifecycle counters ──────────────────────────────────────────────
  // The numbers on the segment pills come from the server's facet endpoint
  // (`lifecycle_counts` on tab=my-tenders) and are never counted up from the
  // page the browser happens to be showing — that page is one paginated slice
  // of one category, so a browser-side count would be wrong by construction.
  //
  // `countsVersion` is bumped by refreshTenders after every draft action, so a
  // published or deleted draft moves the numbers immediately.
  const [countsVersion, setCountsVersion] = useState(0);
  useEffect(() => {
    if (activeMainTab !== "my-tenders") {
      setLifecycleCounts(null);
      return undefined;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const response = await tenderService.getTenderFilters({
          surface: "classic",
          tab: "my-tenders",
          // Same reason as the list query above: this effect only runs for
          // My Requests, where the TP / TP Lite switch is not offered, so the
          // Active / Drafts counts must be the club's own (non-lite) rows and
          // not whatever source was last picked on All Listed.
          is_lite:
            effectiveSourceTab(activeMainTab, activeSubTab) ===
            "tp-lite-tenders",
          signal: controller.signal,
        });
        if (response.success) {
          setLifecycleCounts(response.data?.lifecycle_counts || null);
        }
      } catch (error) {
        if (error?.name !== "CanceledError") {
          console.error("Error fetching lifecycle counts:", error);
        }
      }
    })();
    return () => controller.abort();
  }, [activeMainTab, activeSubTab, countsVersion]);

  // Refetch list + counters after a draft was published, scheduled or deleted.
  // `lastFetchedTenderParams` is cleared first, otherwise the identical-params
  // guard would swallow the very refetch that is supposed to prove the change.
  const refreshTenders = useCallback(() => {
    lastFetchedTenderParams.current = "";
    fetchTenders(pagination.currentPage);
    setCountsVersion((v) => v + 1);
  }, [fetchTenders, pagination.currentPage]);

  const handleFilterChange = (newFilters) => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setFilters(newFilters);
    setSearchTerm(newFilters.search || "");
    setShowFilterModal(false);
  };

  const clearAllFilters = () => {
    const defaultFilters = {
      market_value_from: "",
      market_value_to: "",
      division: "",
      position: "",
      nationality: "",
      min_age: "",
      max_age: "",
      status: "all",
      search: "",
      only_eu_player: false,
      only_free_players: false,
      sort_by: "",
      country: "",
      continent: "",
      type_of_tender: "all",
    };
    setFilters(defaultFilters);
    setSearchTerm("");
    setSortBy("pro");
    setActivePositionFilter("players");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    const activeFilterCount = [
      filters.market_value_from,
      filters.market_value_to,
      filters.division,
      filters.position,
      filters.nationality,
      filters.min_age,
      filters.max_age,
      filters.status !== "all",
      filters.search,
      filters.only_eu_player,
      filters.only_free_players,
      filters.sort_by,
      filters.country,
      filters.continent,
      filters.type_of_tender !== "all",
    ].filter(Boolean).length;

    return activeFilterCount;
  };

  const handleSortChange = (value) => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSortBy(value || "pro");
  };

  // Switching lifecycle tab is a new query, so it starts at page one — the
  // same rule the category pills and the sort dropdown already follow.
  const handleLifecycleChange = (nextTab) => {
    if (!nextTab || nextTab === lifecycleTab) return;
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setLifecycleTab(nextTab);
  };

  const handlePositionFilterChange = (filterId) => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    const safeFilterId = filterId || "players";
    setActivePositionFilter(safeFilterId);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Optimistically reflect a save/unsave toggle in the current list, mirroring
  // the agent flow. On the Saved tab an unsaved tender drops out of the list.
  const handleSaveStatusChange = (tenderId, isSaved) => {
    if (activeMainTab === "saved" && !isSaved) {
      setTenders((prev) => prev.filter((tender) => tender.id !== tenderId));
      return;
    }
    setTenders((prev) =>
      prev.map((tender) =>
        tender.id === tenderId ? { ...tender, is_saved: isSaved } : tender
      )
    );
  };

  // Save/unsave from the list view (mirrors the agent list-view bridge).
  const [listSavingIds, setListSavingIds] = useState(new Set());
  const handleListToggleSave = async (tender) => {
    if (listSavingIds.has(tender.id)) return;
    setListSavingIds((prev) => new Set([...prev, tender.id]));
    try {
      const response = tender.is_saved
        ? await tenderService.unsaveTender(tender.id)
        : await tenderService.saveTender(tender.id);
      if (response.success) {
        handleSaveStatusChange(tender.id, !tender.is_saved);
      }
    } catch (error) {
      console.error("Error saving/unsaving tender:", error);
    } finally {
      setListSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(tender.id);
        return next;
      });
    }
  };

  return {
    countries,
    navigate,
    canCreateTenders,
    activeMainTab,
    setActiveMainTab,
    activeSubTab,
    setActiveSubTab,
    selectedCountry,
    tenders,
    loading,
    error,
    showFilterModal,
    setShowFilterModal,
    transferWindow,
    transferWindowLoading,
    searchQuery,
    setSearchQuery,
    dropdownRef,
    searchInputRef,
    isMobile,
    filters,
    searchTerm,
    sortBy,
    activePositionFilter,
    pagination,
    formatDateRange,
    handleTransferWindowDropdownToggle,
    handleCountrySelect,
    handleFilterChange,
    clearAllFilters,
    hasActiveFilters,
    handleSortChange,
    handlePositionFilterChange,
    handleSearchChange,
    fetchTenders,
    handleSaveStatusChange,
    filteredCountries,
    isTransferWindowDropdownOpen,
    viewMode,
    changeViewMode,
    listSavingIds,
    handleListToggleSave,
    lifecycleTab,
    handleLifecycleChange,
    lifecycleCounts,
    refreshTenders,
  };
};

